import React, { useEffect, useState, useRef, useMemo } from 'react';
import './styles/main.scss';
import ColumnItem from './components/ColumnItem';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAppSelector, useAppDispatch } from '../../hooks/storeHooks';
import { useParams } from 'react-router-dom';
import { ResetConfig, resetUserPerferences, saveUserPerferences } from '../../dispatchers/UserPreferenceDispatcher';
import SuccessfullToast from '../../features/common/toast/SuccessfullToast';
import ErrorToast from '../../features/common/toast/ErrorToast';
import { updateColumnData } from '../slices/ColumnSelectorSlice';
import ResetButton from './ResetButton';
import '../../well-chart/WellChart.css';
import { ReactComponent as DualDotsIcon } from '../../images/dots-dual.svg';
import { createPortal } from 'react-dom';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import useConfig from '../../hooks/useConfig';
import Helpers from '../../utilities/Helpers';

interface ColumnData {
  id: string;
  name: string;
  selected: boolean;
  iconUrl: React.ReactNode;
  LineType?: string;
  LineColor?: string;
  LineWeight?: number;
  MarkerType?: string;
  MarkerSize?: number;
  MarkerColor?: string;
  DisplayOrder?: string;
}

interface ExistingColumn {
  id: string;
  Header: string;
  isVisible: boolean;
  key: string;
  LineType?: string;
  LineColor?: string;
  LineWeight?: number;
  MarkerType?: string;
  MarkerSize?: number;
  MarkerColor?: string;
  DisplayOrder?: string;
}

export default function Main({
  columnsData,
  onUpdateColumns,
  onClose,
  onApplyClickedInColumn,
  saveConfigHasbeenClicked,
  resetToDefaultSystemClicked,
  tableName,
  isChartConfig,
}: {
  columnsData: ExistingColumn[];
  onUpdateColumns: (updated: ExistingColumn[]) => void;
  onClose?: () => void;
  onApplyClickedInColumn?: (configUpdates?: any, columns?: any, saveConfig?: boolean) => void;
  resetToDefaultSystemClicked?: () => void;
  saveConfigHasbeenClicked: (config: boolean) => void;
  tableName?: string;
  isChartConfig: boolean;
}) {
  const mappedData: ColumnData[] = columnsData.map((item) => ({
    id: item.id,
    name: Helpers.toSentenceCase(item.Header),
    selected: item.isVisible,
    iconUrl: <DualDotsIcon />,
    LineColor: item.LineColor,
    MarkerType: item.MarkerType,
    DisplayOrder: item.DisplayOrder,
    LineType: item.LineType,
    LineWeight: item.LineWeight,
    MarkerColor: item.MarkerColor,
    MarkerSize: item.MarkerSize,
  }));

  const dashboardNameFromSelector = useAppSelector((state) => state.AssetAnalysis.chartType);
  const { assetId } = useParams<{ assetId: string }>();
  const [columns, setColumns] = useState<ColumnData[]>([...mappedData]);
  const [selectAll, setSelectAll] = useState(mappedData.every((col) => col.selected));
  const [configUpdates, setConfigUpdates] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saveConfigResponse, setSaveConfigResponse] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // const [setIsApplyClicked] = useState<boolean>(false);

  const convertToExistingColumns = (data: ColumnData[]): ExistingColumn[] => {
    return data.map((col) => ({
      id: col.id,
      Header: Helpers.toSentenceCase(col.name),
      isVisible: col.selected,
      key: col.id,
      LineColor: col.LineColor,
      MarkerType: col.MarkerType,
      DisplayOrder: col.DisplayOrder,
      MarkerSize: col.MarkerSize,
      LineWeight: col.LineWeight,
      LineType: col.LineType,
      MarkerColor: col.MarkerColor,
    }));
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const updatedColumns = columns.map((column) => ({
      ...column,
      selected: newSelectAll,
    }));

    setColumns(updatedColumns);
    onUpdateColumns(convertToExistingColumns(updatedColumns));
  };

  const handleColumnSelect = (id: string) => {
    const updatedColumns = columns.map((column) =>
      column.id === id ? { ...column, selected: !column.selected } : column,
    );

    setColumns(updatedColumns);
    onUpdateColumns(convertToExistingColumns(updatedColumns));

    const allSelected = updatedColumns.every((column) => column.selected);
    setSelectAll(allSelected);
  };
  const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);

  const isRLDashboard = dashboardNameFromSelector?.toLowerCase().includes('rl');
  const { enablePumpChecker } = useConfig();
  const effectivePumpChecker = enablePumpChecker && !isRLDashboard;
  const wellTestWidget =
    tableName === 'Well Test'
      ? effectivePumpChecker
        ? pumpCheckerPageData?.find((obj: any) => obj.key === 'wellTestTable')
        : pumpCheckerPageData?.find((obj: any) => obj.key === 'defaultWellTestTable')
      : pumpCheckerPageData?.find((obj: any) => obj.key === 'wellTestTable');

  // const wellTestWidget = pumpCheckerPageData?.find((obj: any) => obj.key === 'wellTestTable');
  const taskWidget = pumpCheckerPageData?.find((w: any) => w.key === 'taskDetails');
  const cardViewerWidget = pumpCheckerPageData?.find((obj: any) => obj.key === 'cardViewer');
  const timeseriesChartData = pumpCheckerPageData?.find((obj: any) => obj.key === 'timeseriesChart');
  const detailDowntimeChartData =
    tableName === 'downtime-dailysummarytable'
      ? pumpCheckerPageData?.find((obj: any) => obj.key === 'downtime-dailysummarytable')
      : pumpCheckerPageData?.find((obj: any) => obj.key === 'downtime-detailtable');

  const moveColumn = (dragIndex: number, hoverIndex: number) => {
    const draggedColumn = columns[dragIndex];
    const newColumns = [...columns];
    newColumns.splice(dragIndex, 1);
    newColumns.splice(hoverIndex, 0, draggedColumn);
    // Updated the DisplayOrder property based on the new index
    newColumns.forEach((column, index) => {
      column.DisplayOrder = index.toString();
    });
    setColumns(newColumns);
    onUpdateColumns(convertToExistingColumns(newColumns));
  };

  const filteredColumns = columns.filter((column) => column.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const dispatch = useAppDispatch();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setColumns([...mappedData]);
  }, [columnsData]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        if (onClose) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleResetToDefault = async (widgetObj: any, dashboardName?: string) => {
    const widgetData = isChartConfig
      ? timeseriesChartData
      : widgetObj?.key === 'taskDetails'
        ? taskWidget
        : widgetObj?.key === 'cardViewer'
          ? cardViewerWidget
          : widgetObj?.key === 'downtime-detailtable' || widgetObj?.key === 'downtime-dailysummarytable'
            ? detailDowntimeChartData
            : wellTestWidget;

    // Add defensive checks
    if (!widgetData || (!isChartConfig && !widgetData.widgetProperty)) {
      console.error('Widget configuration not found:', widgetData);
      return;
    }

    const resetConfig: ResetConfig = isChartConfig
      ? {
        dashboardName: dashboardName || dashboardNameFromSelector,
        widgetName: 'timeseriesChart',
        ...((dashboardNameFromSelector === 'rl-well-charts' ||
          dashboardNameFromSelector === 'facility-well-charts') &&
          assetId
          ? { assetGUID: assetId }
          : {}),
      }
      : {
        dashboardName: widgetData.widgetProperty.dashboard,
        widgetName: widgetData.widgetProperty.key,
      };

    try {
      const resultAction = await dispatch(resetUserPerferences(resetConfig));

      if (resultAction) {
        if (resetToDefaultSystemClicked) {
          resetToDefaultSystemClicked();
          if (!isChartConfig && dashboardNameFromSelector === 'rl-well-charts') {
            setSelectAll(true);
          }
        }
        if (!isChartConfig) {
          dispatch(updateColumnData(resultAction.payload));
        }

        setSaveConfigResponse(true);
        setSuccessMessage('Successfully reset to system default.');
        setTimeout(() => {
          setSaveConfigResponse(false);
        }, 3000);
      } else {
        setErrorMessage('Error resetting to system default. Please try again!');
      }
    } catch (error) {
      console.error('Unexpected error during dispatch:', error);
      setErrorMessage('Unexpected error occurred. Please try again!');
    }
  };
  const handleParameterUpdate = (id: string, updates: Partial<Parameter>, applyNow = false) => {
    const updatedColumns = columns.map((param) => (param.id === id ? { ...param, ...updates } : param));
    setColumns(updatedColumns);

    const updatedConfig = [...configUpdates.filter((config: any) => config.id !== id), { id, ...updates }];
    setConfigUpdates(updatedConfig);

    // Notify parent of the updated columns
    onUpdateColumns(convertToExistingColumns(updatedColumns));

    // If applyNow is true, apply immediately
    if (applyNow && onApplyClickedInColumn && isChartConfig) {
      onApplyClickedInColumn(updatedConfig, updatedColumns);
    }
  };

  const handleApplyClicked = () => {
    if (onApplyClickedInColumn) {
      if (isChartConfig) {
        onApplyClickedInColumn(configUpdates, columns);
      } else {
        onApplyClickedInColumn();
      }
    }
  };

  const showSaveResetButtons = useMemo(() => {
    const widgetData = isChartConfig
      ? timeseriesChartData
      : tableName === 'taskDetails'
        ? taskWidget
        : tableName === 'cardViewer'
          ? cardViewerWidget
          : tableName === 'downtime-detailtable' || tableName === 'downtime-dailysummarytable'
            ? detailDowntimeChartData
            : wellTestWidget;

    return widgetData?.widgetProperty?.key?.length > 0;
  }, [tableName, isChartConfig]);

  const showSaveConfig = useMemo(() => {
    return (
      showSaveResetButtons &&
      (!(!dashboardNameFromSelector || dashboardNameFromSelector.toLowerCase().includes('realtime')) ||
        tableName === 'downtime-detailtable' ||
        tableName === 'taskDetails' ||
        tableName === 'downtime-dailysummarytable') &&
      tableName !== 'Real-Time-well-Test'
    );
  }, [showSaveResetButtons, dashboardNameFromSelector, tableName]);

  const isAtleastOneColumnSelected = React.useMemo(
    () => columns && columns.length > 0 && columns.some((details) => details.selected),
    [columns],
  );

  const fetchData = async () => {
    // await dispatch(fetchLicenseAccessDetails());
    if (tableName === 'downtime-detailtable' || tableName === 'downtime-dailysummarytable') {
      await dispatch(fetchColumnsData('Downtime'));
    } else {
      const chartType =
        tableName === 'notes'
          ? 'notes'
          : tableName === 'taskDetails' && dashboardNameFromSelector === ''
            ? 'tasks'
            : dashboardNameFromSelector;
      if (!chartType) return;
      if ((chartType === 'rl-well-charts' || chartType === 'facility-well-charts') && assetId) {
        await dispatch(fetchColumnsData({ chartType, assetGUID: assetId }));
      } else {
        await dispatch(fetchColumnsData(chartType));
      }
    }
  };

  const saveConfiguration = async () => {
    if (isChartConfig) {
      // saveConfigHasbeenClicked(true);
      if (onApplyClickedInColumn) {
        onApplyClickedInColumn(configUpdates, columns, true);
      }
      return;
    }

    try {
      const widgetData =
        tableName === 'taskDetails'
          ? taskWidget
          : tableName === 'cardViewer'
            ? cardViewerWidget
            : tableName === 'downtime-detailtable' || tableName === 'downtime-dailysummarytable'
              ? detailDowntimeChartData
              : wellTestWidget;

      const transformedWidget = {
        DashboardName: widgetData?.widgetProperty?.dashboard ?? '',
        WidgetName: widgetData?.widgetProperty?.key,
        PropertyType: 'Columns',
        Preferences: convertToExistingColumns(columns).map((col: any, index: number) => ({
          Order: index + 1,
          Label: col.Header,
          Key: col.id,
          Selected: col.isVisible,
          Visible: true,
        })),
        ...((dashboardNameFromSelector === 'rl-well-charts' || dashboardNameFromSelector === 'facility-well-charts') &&
          assetId
          ? { assetGUID: assetId }
          : {}),
      };
      await dispatch(saveUserPerferences(transformedWidget));
      setSaveConfigResponse(true);
      setSuccessMessage('Successfully saved configuration.');
      fetchData();
      setTimeout(() => {
        setSaveConfigResponse(false);
      }, 3000);

      saveConfigHasbeenClicked(false);
      handleApplyClicked();
    } catch (err) {
      console.error('Error saving column configuration', err);
    }
  };

  return createPortal(
    <div className='main-container-panel' ref={modalRef}>
      <SuccessfullToast
        isShowEnabled={saveConfigResponse}
        message={successMessage}
        callback={() => setSuccessMessage('')}
      />
      <ErrorToast isShowEnabled={!!errorMessage} message={errorMessage} callback={() => setErrorMessage('')} />
      <div className='main-container__wrapper'>
        <header className='header'>
          <div className='header__logo-container'>
            <div className='header__logo-icon'>
              <div className='header__logo-icon-inner cursor-pointer' onClick={onClose} />
            </div>

            <div className='header__logo-text'>
              <h4 className='column-header'>Columns</h4>
            </div>
          </div>
        </header>

        <section className='toolbar'>
          <div className='toolbar__select-all'>
            <div className='toolbar__select-all-container'>
              <div className='toolbar__select-all-inner'>
                <div className='toolbar__select-all-label'>
                  <div className={`checkbox ${selectAll ? 'checkbox--selected' : ''}`} onClick={handleSelectAll}>
                    {selectAll && (
                      <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                          d='M11.6666 3.5L5.24992 9.91667L2.33325 7'
                          stroke='white'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                  <span className='text'>Select all</span>
                </div>
              </div>
            </div>
          </div>
          <div className='toolbar__search'>
            <div className='toolbar__search-container'>
              <div className='toolbar__search-inner'>
                <div className='toolbar__search-input-container'>
                  <div className='toolbar__search-input-wrapper relative'>
                    <div className='ico-search' />
                    <input
                      type='text'
                      className='toolbar__search-input pr-[24px]'
                      placeholder='Search'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        type='button'
                        aria-label='Clear search'
                        onClick={() => setSearchTerm('')}
                        className='absolute right-[6px] top-1/2 -translate-y-1/2 text-[#b8c5cc] hover:text-white text-[14px] leading-none bg-transparent border-none cursor-pointer p-0 m-0'
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <main className='main-content'>
          <DndProvider backend={HTML5Backend}>
            <ul className='column-list mb-[60px]'>
              {filteredColumns.map((column, index) => (
                <ColumnItem
                  key={column.id.toString() + index.toString()}
                  index={index}
                  id={column.id}
                  name={column.name}
                  selected={column.selected}
                  iconUrl={column.iconUrl}
                  onSelect={() => handleColumnSelect(column.id)}
                  moveColumn={moveColumn}
                  handleParameterUpdate={handleParameterUpdate}
                  LineColor={column.LineColor}
                  LineType={column.LineType}
                  LineWeight={column.LineWeight}
                  MarkerType={column.MarkerType}
                  MarkerSize={column.MarkerSize}
                  MarkerColor={column.MarkerColor}
                />
              ))}
            </ul>
          </DndProvider>
        </main>

        <footer className='footer' style={{ zIndex: 99 }}>
          <div
            className={`footer__container ${!showSaveConfig ? 'justify-end' : ''}`}
            style={{
              display: 'flex',
              // justifyContent: 'flex-end', //  align to right
              alignItems: 'center',
              paddingLeft: '20px',
            }}
          >
            {showSaveConfig && (
              <button
                onClick={() => {
                  saveConfiguration();
                }}
                className='footer-button footer-button__submit'
              >
                Save configuration
              </button>
            )}

            <div className='flex gap-3'>
              {showSaveConfig && (
                <ResetButton
                  widget={
                    isChartConfig
                      ? timeseriesChartData
                      : tableName === 'taskDetails'
                        ? taskWidget
                        : tableName === 'cardViewer'
                          ? cardViewerWidget
                          : tableName === 'downtime-detailtable' || tableName === 'downtime-dailysummarytable'
                            ? detailDowntimeChartData
                            : wellTestWidget
                  }
                  dashboardName={
                    isChartConfig
                      ? timeseriesChartData?.widgetProperty?.dashboard
                      : tableName === 'taskDetails'
                        ? taskWidget?.widgetProperty?.dashboard
                        : tableName === 'cardViewer'
                          ? cardViewerWidget?.widgetProperty?.dashboard
                          : tableName === 'downtime-detailtable' || tableName === 'downtime-dailysummarytable'
                            ? detailDowntimeChartData?.widgetProperty?.dashboard
                            : wellTestWidget?.widgetProperty?.dashboard
                  }
                  handleResetToDefault={handleResetToDefault}
                  isChartConfig={isChartConfig}
                />
              )}
              <button
                analytics-id='column-selector-apply-btn'
                className={`footer__apply-button ${!isAtleastOneColumnSelected ? 'footer__apply-button-disabled' : ''}`}
                onClick={handleApplyClicked}
                disabled={!isAtleastOneColumnSelected}
              >
                <span className='footer__apply-button-text'>Apply</span>
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>,
    document.querySelector('.outlet-container') || document.body,
  );
}
